import { useState, useCallback } from 'react'
import { useSnackbar } from 'notistack'
import { ProgressOperation, ProgressStep } from '../components/ui/ProgressIndicator'

export interface BulkOperationConfig {
  title: string
  description?: string
  totalItems: number
  batchSize?: number
  canCancel?: boolean
  canRetry?: boolean
}

export interface BulkOperationResult {
  successful: number
  failed: number
  warnings: number
  errors: string[]
}

type BulkOperationHandler<T> = (
  items: T[],
  onProgress: (processed: number, step?: string) => void,
  signal?: AbortSignal
) => Promise<BulkOperationResult>

export const useBulkOperations = () => {
  const [operations, setOperations] = useState<ProgressOperation[]>([])
  const { enqueueSnackbar } = useSnackbar()

  const createOperation = useCallback((config: BulkOperationConfig): ProgressOperation => {
    const id = `bulk_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    
    return {
      id,
      title: config.title,
      description: config.description,
      status: 'pending',
      progress: 0,
      steps: [],
      startTime: new Date(),
      totalItems: config.totalItems,
      processedItems: 0,
      successCount: 0,
      errorCount: 0,
      warningCount: 0,
      canCancel: config.canCancel ?? true,
      canRetry: config.canRetry ?? true,
    }
  }, [])

  const updateOperation = useCallback((id: string, updates: Partial<ProgressOperation>) => {
    setOperations(prev => prev.map(op => 
      op.id === id ? { ...op, ...updates } : op
    ))
  }, [])

  const addStep = useCallback((operationId: string, step: ProgressStep) => {
    setOperations(prev => prev.map(op => 
      op.id === operationId 
        ? { ...op, steps: [...op.steps, step] }
        : op
    ))
  }, [])

  const updateStep = useCallback((operationId: string, stepId: string, updates: Partial<ProgressStep>) => {
    setOperations(prev => prev.map(op => 
      op.id === operationId 
        ? {
            ...op,
            steps: op.steps.map(step => 
              step.id === stepId ? { ...step, ...updates } : step
            )
          }
        : op
    ))
  }, [])

  const executeBulkOperation = useCallback(async <T>(
    items: T[],
    config: BulkOperationConfig,
    handler: BulkOperationHandler<T>
  ): Promise<BulkOperationResult> => {
    const operation = createOperation(config)
    setOperations(prev => [...prev, operation])

    const abortController = new AbortController()
    
    // Add cancel functionality
    const cancelOperation = () => {
      abortController.abort()
      updateOperation(operation.id, {
        status: 'cancelled',
        endTime: new Date(),
      })
    }

    // Add retry functionality
    const retryOperation = () => {
      updateOperation(operation.id, {
        status: 'running',
        progress: 0,
        processedItems: 0,
        successCount: 0,
        errorCount: 0,
        warningCount: 0,
        steps: [],
        startTime: new Date(),
        endTime: undefined,
      })
      
      executeBulkOperation(items, config, handler)
    }

    updateOperation(operation.id, {
      status: 'running',
      onCancel: cancelOperation,
      onRetry: retryOperation,
    })

    try {
      // Add initial step
      addStep(operation.id, {
        id: 'init',
        label: 'Initializing operation',
        status: 'running',
        startTime: new Date(),
      })

      const onProgress = (processed: number, stepMessage?: string) => {
        const progress = Math.round((processed / config.totalItems) * 100)
        
        updateOperation(operation.id, {
          progress,
          processedItems: processed,
        })

        if (stepMessage) {
          addStep(operation.id, {
            id: `step_${processed}`,
            label: stepMessage,
            status: 'running',
            startTime: new Date(),
          })
        }
      }

      // Execute the bulk operation
      const result = await handler(items, onProgress, abortController.signal)

      // Mark initialization step as complete
      updateStep(operation.id, 'init', {
        status: 'completed',
        endTime: new Date(),
      })

      // Update final operation status
      const finalStatus = result.failed > 0 ? 'error' : 'completed'
      
      updateOperation(operation.id, {
        status: finalStatus,
        progress: 100,
        processedItems: config.totalItems,
        successCount: result.successful,
        errorCount: result.failed,
        warningCount: result.warnings,
        endTime: new Date(),
      })

      // Show notification
      if (result.failed === 0) {
        enqueueSnackbar(
          `Successfully processed ${result.successful} items`,
          { variant: 'success' }
        )
      } else {
        enqueueSnackbar(
          `Completed with ${result.successful} successful, ${result.failed} failed`,
          { variant: 'warning' }
        )
      }

      return result

    } catch (error) {
      if (abortController.signal.aborted) {
        // Operation was cancelled
        return { successful: 0, failed: 0, warnings: 0, errors: ['Operation cancelled'] }
      }

      // Handle error
      updateOperation(operation.id, {
        status: 'error',
        endTime: new Date(),
      })

      addStep(operation.id, {
        id: 'error',
        label: 'Operation failed',
        status: 'error',
        message: error instanceof Error ? error.message : 'Unknown error',
        startTime: new Date(),
        endTime: new Date(),
      })

      enqueueSnackbar(
        `Operation failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
        { variant: 'error' }
      )

      throw error
    }
  }, [createOperation, updateOperation, addStep, updateStep, enqueueSnackbar])

  const clearCompletedOperations = useCallback(() => {
    setOperations(prev => prev.filter(op => 
      op.status === 'running' || op.status === 'pending'
    ))
  }, [])

  const clearAllOperations = useCallback(() => {
    setOperations(prev => prev.filter(op => op.status === 'running'))
  }, [])

  const removeOperation = useCallback((id: string) => {
    setOperations(prev => prev.filter(op => op.id !== id))
  }, [])

  return {
    operations,
    executeBulkOperation,
    clearCompletedOperations,
    clearAllOperations,
    removeOperation,
    updateOperation,
  }
}

export default useBulkOperations